<template>
  <!--
    Single deterministic per-row spacing source: py-2.5 (20px) lives INSIDE the
    measured item (vue-virtual-scroller is blind to flex `gap`, so callers must
    NOT add a container gap or an extra pb-* wrapper — that double-counts and
    de-syncs estimate vs measured). `contain:layout style` isolates this row's
    reflow so a late content/translation/avatar change can't shift siblings.
  -->
  <div class="py-2.5 [contain:layout_style]">
    <!-- Removed message placeholder -->
    <div
      v-if="isRemoved"
      class="px-3 py-2 rounded-[15px] bg-surface text-text-tertiary text-sm italic"
      :data-message="message.id"
    >
      {{ $t("channel.chat.moderation.removed") }}
    </div>

    <!-- Normal message display -->
    <div
      v-else
      :tabindex="0"
      class="text-sm w-full mx-0 flex flex-col justify-center focus-visible:ring-1 focus-visible:ring-brand-primary rounded-[15px] select-none outline-none"
      :data-message="message.id"
      :class="[
        isReplying && !isReplyingToMessage && 'opacity-50 ',
        isReplying && isReplyingToUpstream && 'bg-surface ',
        isReply && 'bg-brand-bgdivider-20 ',
        isUnderReview && 'opacity-60',

        message.isQuestion &&
          'rounded-[15px] border-2 border-border-brand  p-2',
        message.isQuestion &&
          (isMobile ? 'bg-bg-secondary' : 'bg-fill-brand-secondary'),
      ]"
      @click.stop="tryToReply"
      @keyup.space.stop="tryToReply"
    >
      <button
        v-if="message.replyTo"
        class="block flex-1 p-1 bg-brand-bgdivider-20 rounded-sm rounded-t-[15px] focus:outline-none"
        @click.stop="emit('parentReply', message.replyTo.id)"
      >
        <div
          class="flex w-full break-words text-sm text-clip whitespace-pre-line text-left"
        >
          <Avatar
            :key="message.replyTo.id"
            :user="message.replyTo.author"
            :size="isReply ? '2xs' : 'xs'"
            class="mr-2 ml-0.5"
            :vip-border="message.replyTo.author.hasPaidMembership"
          />

          <div class="flex flex-col justify-start items-start">
            <div
              class="text-text-secondary text-sm font-semibold leading-[18px]"
            >
              {{ message.replyTo.author.username }}
            </div>
            <div
              class="self-stretch text-text text-sm font-medium leading-[18px] break-good"
              :class="
                isReplyingToMessage || isReplyingToUpstream
                  ? 'text-basics-primary'
                  : 'text-basics-tertiary-60'
              "
            >
              <ChatMessageText
                :text="replyToMessageContent ?? ''"
                :interactive="false"
              />
            </div>
          </div>
        </div>
      </button>

      <div class="flex flex-row items-start gap-1">
        <div
          class="flex w-full break-words text-sm text-clip whitespace-pre-line"
          :class="{
            'pl-1 py-1': message.replyTo,
          }"
        >
          <Avatar
            :key="message.author.id"
            :user="message.author"
            :size="isReply ? '2xs' : 'xs'"
            class="mr-2 ml-0.5"
            :vip-border="message.author.hasPaidMembership"
          />

          <div class="flex flex-col justify-start items-start">
            <div
              class="text-text text-sm font-medium leading-[18px] saturate-50"
            >
              <div
                v-if="message.isQuestion"
                class="text-text-secondary font-sans text-sm italic font-light leading-5"
              >
                {{ $t("channel.chat.questionFrom") }}
              </div>
              <div class="flex flex-wrap gap-x-3 items-center">
                <UsernameWithBadge
                  :username="message.author.username"
                  :is-vip="message.author.hasPaidMembership"
                  class="mb-0.5"
                />
                <CountryBadge :icon="message.author.countryIso" />
                <span
                  class="text-text-tertiary text-opacity-60 font-sans text-sm font-medium"
                >
                  {{ timeAgo }}
                </span>
              </div>
            </div>
            <div
              class="self-stretch text-text text-[13px] font-medium leading-4 break-good"
              :class="
                isReplyingToMessage || isReplyingToUpstream
                  ? 'text-basics-primary'
                  : 'text-basics-tertiary-60'
              "
            >
              <ChatMessageText :text="messageContent ?? ''" />
            </div>

            <TranslateButton
              v-if="canShowTranslateButton"
              v-model:auto-translate="localAutoTranslate"
              :has-translation="hasTranslation"
              :is-translated="isTranslated"
            />

            <span
              v-if="isUnderReview && canSeeUnderReview"
              class="text-text-tertiary text-xs italic mt-1"
            >
              {{ $t("channel.chat.moderation.underReview") }}
            </span>
          </div>
        </div>

        <button
          class="bg-transparent border-transparent whitespace-nowrap flex flex-col items-center text-brand-primary w-8 px-2 disabled:opacity-40 disabled:cursor-not-allowed"
          :class="[message.replyTo && 'py-1', likes <= 0 && 'px-2']"
          variant="icon"
          size="sm"
          :disabled="!message.isVerified"
          @click.stop="onMessageLike(message.id, !isLiked)"
        >
          <Icon
            :icon="isLiked ? 'mdi:heart' : 'mdi:heart-outline'"
            class="size-4 box-content"
          />

          <p v-if="likes > 0" class="text-2xs leading-3">
            {{ formatCompactNumber(likes, userStore.userLocale) }}
          </p>
        </button>

        <DropdownMenuRoot v-if="showActionMenu">
          <DropdownMenuTrigger
            :aria-label="$t('channel.chat.messageActions')"
            as-child
            @click.stop
          >
            <button :class="[message.replyTo && 'py-1']">
              <Icon
                icon="mdi:more-vert"
                class="w-6 text-text-brand cursor-pointer"
              />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuPortal>
            <DropdownMenuContent
              side="bottom"
              :side-offset="5"
              class="outline-none z-[99999999] w-[246px] mr-5 p-2 bg-fill-brand-secondary rounded-xl backdrop-blur-[70px] flex flex-col items-center"
            >
              <!-- Host-only actions -->
              <DropdownMenuItem
                v-if="hasDeleteMessagePermission"
                class="flex items-center select-none outline-none data-[highlighted]:bg-fill-brand-secondary-hover text-white rounded-lg px-2 py-2 gap-2 w-full text-sm font-medium font-sans leading-tight cursor-pointer"
                @click="onPinMessage(true)"
              >
                <Icon icon="mdi:pin" width="18" height="18" />
                {{ $t("channel.chat.pinMessage") }}
              </DropdownMenuItem>

              <DropdownMenuItem
                v-if="hasDeleteMessagePermission"
                class="flex items-center select-none outline-none data-[highlighted]:bg-fill-brand-secondary-hover text-white rounded-lg px-2 py-2 gap-2 w-full text-sm font-medium font-sans leading-tight cursor-pointer"
                @click.stop="onDeleteMessage"
              >
                <Icon icon="ph:trash" width="18" height="18" />
                {{ $t("channel.chat.deleteMessage") }}
              </DropdownMenuItem>

              <!-- Report action (all authenticated users, not own messages) -->
              <DropdownMenuItem
                v-if="canReport"
                class="flex items-center select-none outline-none data-[highlighted]:bg-fill-brand-secondary-hover text-white rounded-lg px-2 py-2 gap-2 w-full text-sm font-medium font-sans leading-tight cursor-pointer"
                @click="reportModalOpen = true"
              >
                <Icon icon="fluent:flag-16-regular" width="18" height="18" />
                {{ $t("channel.chat.report.action") }}
              </DropdownMenuItem>

              <DropdownMenuArrow class="fill-fill-brand-secondary" />
            </DropdownMenuContent>
          </DropdownMenuPortal>
        </DropdownMenuRoot>

        <!-- Spacer keeps the flex row geometry identical when there is no action menu -->
        <div v-else aria-hidden="true" class="w-6" />
      </div>
    </div>

    <!-- Report modal -->
    <ConfirmationModalRoot v-model="reportModalOpen">
      <template #default>
        <ConfirmationModalTitle>
          {{ $t("channel.chat.report.title") }}
        </ConfirmationModalTitle>
        <p class="text-basics-tertiary-60 text-sm mt-1">
          {{ $t("channel.chat.report.description") }}
        </p>
        <textarea
          v-model="reportReason"
          :placeholder="$t('channel.chat.report.reasonPlaceholder')"
          maxlength="1000"
          rows="3"
          class="w-full mt-3 p-2 rounded-lg bg-brand-bgdivider-20 text-basics-primary text-sm placeholder:text-basics-tertiary-60 resize-none outline-none focus:ring-1 focus:ring-brand-primary"
        />
      </template>

      <template #actions>
        <ButtonV2
          variant="primary"
          size="sm"
          :disabled="isSubmittingReport"
          @click="submitReport"
        >
          {{ $t("channel.chat.report.submit") }}
        </ButtonV2>
        <ButtonV2 variant="subtle" size="sm" @click="reportModalOpen = false">
          {{ $t("channel.chat.report.cancel") }}
        </ButtonV2>
      </template>
    </ConfirmationModalRoot>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import Avatar from "../me/avatar.vue";
import { Icon } from "@iconify/vue";
import { formatCompactNumber } from "@/utils/format";
import { useUserStore } from "@/stores/user.store";
import { useShowStore } from "@/stores/show.store";
import CountryBadge from "@/components/badge/country-badge.vue";
import {
  DropdownMenuRoot,
  DropdownMenuTrigger,
  DropdownMenuPortal,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuArrow,
} from "reka-ui";
import {
  useLocalAutoTranslate,
  useObjectTranslation,
} from "@/stores/translation.store";
import TranslateButton from "../channel/translate-button.vue";
import type { IChatMessage } from "@/stores/chat/model/chat-model";
import { timeAgoNarrow } from "@/utils/date";
import { useAppStore } from "@/stores/app.store";
import UsernameWithBadge from "@/components/me/username-with-badge.vue";
import ChatMessageText from "./chat-message-text";
import ConfirmationModalRoot from "@/components/ui/modal/confirmation-modal/confirmation-modal-root.vue";
import ConfirmationModalTitle from "@/components/ui/modal/confirmation-modal/confirmation-modal-title.vue";
import ButtonV2 from "@/components/ui/button/button-v2.vue";
import { useChat } from "@/stores/chat/services/use-chat";

const emit = defineEmits<{
  parentReply: [parentId: string];
  reply: [messageId: string];
  like: [messageId: string, liked: boolean];
  delete: [messageId: string];
  setPinnedState: [messageId: string, value: boolean];
}>();
const props = defineProps<{
  message: IChatMessage;
  likes: number;
  isReply: boolean;
  isLiked: boolean;
  isReplying: boolean;
  isReplyingToMessage: boolean;
  isReplyingToUpstream: boolean;
  isMobile: boolean;
}>();

const userStore = useUserStore();
const showStore = useShowStore();
const chat = useChat();

function tryToReply(_args: any) {
  if (props.message.isQuestion) return;
  emit("reply", props.message.id);
}

const hasDeleteMessagePermission = computed(() => showStore.isHost);

const isOwnMessage = computed(
  () => props.message.author.id === userStore.userId,
);

const canReport = computed(
  () => userStore.isAuthenticated && !isOwnMessage.value,
);

const showActionMenu = computed(
  () => hasDeleteMessagePermission.value || canReport.value,
);

// Moderation status checks
const isUnderReview = computed(
  () => props.message.moderationStatus === "under_review",
);

const isRemoved = computed(() => props.message.moderationStatus === "removed");

const canSeeUnderReview = computed(
  () => isOwnMessage.value || hasDeleteMessagePermission.value,
);

function onDeleteMessage() {
  emit("delete", props.message.id);
}

function onPinMessage(value: boolean) {
  emit("setPinnedState", props.message.id, value);
}

function onMessageLike(messageId: string, liked: boolean) {
  emit("like", messageId, liked);
}

// Report state
const reportModalOpen = ref(false);
const reportReason = ref("");
const isSubmittingReport = ref(false);

async function submitReport() {
  isSubmittingReport.value = true;
  try {
    await chat.reportChatMessage(
      props.message.id,
      reportReason.value || undefined,
    );
  } finally {
    isSubmittingReport.value = false;
    reportModalOpen.value = false;
    reportReason.value = "";
  }
}

const localAutoTranslate = useLocalAutoTranslate();

const { text: replyToMessageContent } = useObjectTranslation(
  computed(() => {
    if (!props.message.replyTo) return null;
    return {
      authorUserId: props.message.replyTo.author.id,
      originalText: props.message.replyTo.content,
      objectAccessors: {
        objectId: props.message.replyTo.id,
        objectProperty: "content",
      },
      translatedText: props.message.replyTo.messageTranslation,
      localAutoTranslate: localAutoTranslate.value,
    };
  }),
);

const {
  text: messageContent,
  hasTranslation,
  canShowTranslateButton,
  isTranslated,
} = useObjectTranslation(
  computed(() => ({
    authorUserId: props.message.author.id,
    originalText: props.message.content,
    objectAccessors: {
      objectId: props.message.id,
      objectProperty: "content",
    },
    translatedText: props.message.messageTranslation,
    localAutoTranslate: localAutoTranslate.value,
  })),
);

const appStore = useAppStore();

const timeAgo = computed(
  () =>
    appStore.timer &&
    timeAgoNarrow(props.message.timestamp, userStore.userLocale),
);
</script>
